import React from 'react';
import { css } from '@emotion/react';
import { Alert } from '@material-ui/lab';
import { CircularProgress } from '@material-ui/core';
import { colors } from '@/styles';
import {
    ModalContent,
    ModalTitle,
    PrimaryButton,
    ResponsiveSidebarDialog,
    SecondaryButton,
    StickyModalActions,
} from '@/styles/blocks';
import { DividePrompt } from './board/DividePrompt';
import { ModeSwitch } from './board/ModeSwitch';
import { SplitBoard } from './board/SplitBoard';
import {
    BOARD_MODES,
    CLAIMS_INTRO,
    ITEMS_INTRO,
    READ_ERROR,
    SERVICES_INTRO,
    SPLITTING_LABEL,
    SPLIT_ORDER_LABEL,
} from './constants';
import { useSplitModal } from './hooks';

/** Intro line above the board for each mode. */
const MODE_INTROS = {
    [BOARD_MODES.ITEMS]: ITEMS_INTRO,
    [BOARD_MODES.SERVICES]: SERVICES_INTRO,
    [BOARD_MODES.CLAIMS]: CLAIMS_INTRO,
};

/** Kept off screen but read by screen readers. */
const visuallyHidden = css`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`;
const PROBLEM_TEXT_ID = 'split-order-problems';

/**
 * The Split Order dialog, one screen: the intro for the current mode, any blocker or error alert, the Items |
 * Services | Claims switch (each extra option only when the parent has services or open claims), the split board,
 * and Cancel / Split order. It reads the order
 * itself (network-only, skipped while closed), so it takes only an `orderId`. Split order looks disabled until the
 * layout is valid; clicking it then switches to the mode with a problem. A failed split keeps the layout and shows
 * the server's message. A successful one calls `onSplit` with the parent and the new children, then closes.
 *
 * @param {{ open: boolean, orderId: string, onClose: () => void, onSplit?: (result: { parent: object|null, children: object[] }) => void }} props
 *   `onSplit` runs after a successful split so the caller can refetch the order and say which orders were made.
 */
const SplitOrderModal = ({ open, orderId, onClose, onSplit }) => {
    const {
        order,
        items,
        services,
        claims,
        layout,
        mode,
        setMode,
        loading,
        readError,
        alertLines,
        submitting,
        canSplit,
        splitDisabled,
        problemText,
        notice,
        board,
        pendingMove,
        confirmMove,
        cancelMove,
        handleSplit,
        handleClose,
    } = useSplitModal({ open, orderId, onClose, onSplit });
    // A mode with nothing to place has no option on the switch, so the board falls back to items mode.
    const modeAvailable = {
        [BOARD_MODES.SERVICES]: services.length > 0,
        [BOARD_MODES.CLAIMS]: claims.length > 0,
    };
    const boardMode = modeAvailable[mode] ? mode : BOARD_MODES.ITEMS;

    const renderBody = () => {
        if (loading) {
            return (
                <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        padding: 32px 0;
                    `}
                >
                    <CircularProgress aria-label="Loading order" />
                </div>
            );
        }
        if (readError && !order) {
            return (
                <Alert severity="error" role="alert">
                    {READ_ERROR}
                </Alert>
            );
        }
        if (!order || !layout) return null;
        return (
            <>
                <p
                    css={css`
                        margin: 0 0 12px;
                        color: ${colors.greys[3]};
                        font-size: 13px;
                    `}
                >
                    {MODE_INTROS[boardMode]}
                </p>
                {alertLines.length ? (
                    <Alert
                        severity="error"
                        role="alert"
                        css={css`
                            margin-bottom: 12px;
                        `}
                    >
                        {alertLines.map((line) => (
                            <div key={line}>{line}</div>
                        ))}
                    </Alert>
                ) : null}
                <ModeSwitch
                    mode={boardMode}
                    onChange={setMode}
                    layout={layout}
                    items={items}
                    services={services}
                    claims={claims}
                />
                <SplitBoard
                    layout={layout}
                    items={items}
                    services={services}
                    claims={claims}
                    mode={boardMode}
                    parentOrderNumber={order.order_number}
                    disabled={submitting}
                    {...board}
                />
                <DividePrompt
                    key={pendingMove?.move.portionKey ?? 'closed'}
                    pending={pendingMove}
                    onMove={confirmMove}
                    onCancel={cancelMove}
                    disabled={submitting}
                />
            </>
        );
    };

    const looksDisabled = splitDisabled || !canSplit;

    return (
        <ResponsiveSidebarDialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            {/* No close X while the split runs, like Cancel. */}
            <ModalTitle
                title={`Split Order - ${order?.order_number || ''}`}
                onClose={submitting ? undefined : handleClose}
            />
            <ModalContent
                css={css`
                    width: 100%;
                    box-sizing: border-box;
                `}
            >
                {renderBody()}
            </ModalContent>
            <StickyModalActions
                css={css`
                    justify-content: flex-end;
                    gap: 12px;
                `}
            >
                <span id={PROBLEM_TEXT_ID} css={visuallyHidden}>
                    {problemText}
                </span>
                <span role="status" aria-live="polite" css={visuallyHidden}>
                    {notice}
                </span>
                <SecondaryButton disabled={submitting} onClick={handleClose}>
                    Cancel
                </SecondaryButton>
                {/* Not `disabled` while the layout is only incomplete, so a click can take the user to the problem. */}
                <PrimaryButton
                    disabled={splitDisabled}
                    aria-disabled={looksDisabled}
                    aria-describedby={problemText ? PROBLEM_TEXT_ID : undefined}
                    onClick={handleSplit}
                    css={
                        looksDisabled && !splitDisabled
                            ? css`
                                  opacity: 0.45;
                                  cursor: not-allowed;
                              `
                            : undefined
                    }
                >
                    {submitting ? SPLITTING_LABEL : SPLIT_ORDER_LABEL}
                </PrimaryButton>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default SplitOrderModal;
